const CARBONE_RENDER_URL = "/services/carbone/render/template";
const CARBONE_DOWNLOAD_URL = "/services/carbone/render";

export type RenderOptions = {
  convertTo?: string;
  timezone?: string;
  lang?: string;
};

type RenderResponse = {
  success: boolean;
  data?: {
    renderId: string;
  };
  error?: string;
};

// Carbone reports template errors (bad markers, syntax) as JSON with an
// `error` message that tells the template author exactly what to fix, so
// surface it instead of a generic status-code error.
async function extractCarboneError(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as RenderResponse;
    return typeof body.error === "string" && body.error ? body.error : undefined;
  } catch {
    return undefined;
  }
}

export async function renderTemplate(
  templateDataURI: string,
  data: Record<string, unknown>,
  options: RenderOptions = {},
  signal?: AbortSignal,
): Promise<Blob> {
  // Strip the data URI prefix (e.g. "data:application/...;base64,") to get raw base64
  const base64Template = templateDataURI.split(",")[1];

  if (!base64Template) {
    throw new Error("Invalid template data URI");
  }

  // Step 1: Submit template + data to Carbone, which returns a renderId
  const renderResponse = await fetch(CARBONE_RENDER_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({
      template: base64Template,
      data,
      convertTo: options.convertTo ?? "pdf",
      timezone: options.timezone,
      lang: options.lang,
    }),
    signal,
  });

  if (!renderResponse.ok) {
    const carboneError = await extractCarboneError(renderResponse);
    throw new Error(carboneError ?? `Render failed (${renderResponse.status})`);
  }

  const renderResult = (await renderResponse.json()) as RenderResponse;

  if (!renderResult.success || !renderResult.data?.renderId) {
    throw new Error(renderResult.error ?? "Failed to render template");
  }

  const downloadResponse = await fetch(
    `${CARBONE_DOWNLOAD_URL}/${renderResult.data.renderId}`,
    {
      method: "GET",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
      signal,
    },
  );

  if (!downloadResponse.ok) {
    throw new Error(`Download failed (${downloadResponse.status})`);
  }

  return downloadResponse.blob();
}

export function createBlobUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

export function revokeBlobUrl(url: string): void {
  URL.revokeObjectURL(url);
}

export type TemplateFileResult = {
  dataURI: string;
  fileType: string;
};

export async function fetchTemplateFile(url: string): Promise<TemplateFileResult> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download template (${response.status})`);
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        dataURI: reader.result as string,
        fileType: "docx",
      });
    };
    reader.onerror = () => reject(new Error("Failed to read template file"));
    reader.readAsDataURL(blob);
  });
}
